import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useGridSelector } from "~/features/inventory/useGridSelector";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { usePostFlagBinRemovedMutation } from "~/redux/warehouse/autostoreGrid.hooks";

type RemoveBinModalProps = {
  open: boolean;
  onClose: () => void;
};

export function RemoveBinModal({ open, onClose }: RemoveBinModalProps) {
  const { t } = useTranslation();
  const { warningToast, errorToast } = useToast();
  const { autostoreGridOverride } = useGridSelector();

  const workstationId = useAppSelector(selectWorkstationId);
  const workstationGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const [
    postFlagBinRemoved,
    {
      isLoading: isLoadingPostFlagBinRemoved,
      error: postFlagBinRemovedError,
      isSuccess: isSuccessPostFlagBinRemoved,
      isError: isErrorPostFlagBinRemoved,
      reset: resetMutation
    }
  ] = usePostFlagBinRemovedMutation();

  const {
    register,
    reset: resetForm,
    getValues,
    watch
  } = useForm<{
    binId: number | null;
  }>();
  const watchedBinId = watch("binId");

  const handleClose = () => {
    resetForm({ binId: null });
    resetMutation();
    onClose();
  };

  const handlePostFlagBin = async () => {
    const gridId = workstationGridId || autostoreGridOverride;
    if (!gridId) {
      return errorToast(t("please select autostore grid"));
    }
    const binId = getValues("binId");
    if (!binId) {
      return errorToast(t("please enter bin id"));
    }
    await postFlagBinRemoved({
      autostoreGridId: gridId,
      workstationId: workstationId || null,
      binId
    });
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!open) {
      return;
    }
    if (isSuccessPostFlagBinRemoved) {
      warningToast(t("bin removed success", { binId: getValues("binId") }));
      resetForm({ binId: null });
      resetMutation();
      onClose();
    }
    if (isErrorPostFlagBinRemoved) {
      errorToast(getMessageFromRtkError(postFlagBinRemovedError));
      resetMutation();
    }
    // intentionally omitting bin Id here to prevent multiple error toasts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isSuccessPostFlagBinRemoved,
    warningToast,
    t,
    isErrorPostFlagBinRemoved,
    postFlagBinRemovedError,
    errorToast,
    onClose,
    resetForm,
    resetMutation
  ]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("remove bin")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("bin removed warning")}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          aria-label={t("bin removed input aria label")}
          placeholder={t("bin number")}
          {...register("binId")}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("cancel")}</Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => void handlePostFlagBin()}
          disabled={!watchedBinId || isLoadingPostFlagBinRemoved}
        >
          {t("remove")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
